import * as React from 'react';
import { BriefcaseIcon, CheckMarkIcon, LockIcon, PersonIcon, PhoneIcon, WarningIcon } from '../common/Icons';
import { ChangePassword } from '../../models/user/user';
import { UserAttributeName } from '../../models/user/attribute';
import { authenticationService } from '../../services/authentication';
import { EventManager } from '../../services/EventManager';
import { LogManager } from '../../utils/LogManager';
import { errorHandler } from '../../services/errorHandler';
import { Attributes } from 'fs-api-client';
import { injectProfile, ProfileInjectorProperties } from '../hocs/ProfileInjector';
import { WebportalEvent } from '../../services/WebportalEvent';
import { CognitoException, CognitoResetPasswordExceptionMessage } from '../authentication/errors/CognitoErrors';
import { DEFAULT_ERROR_MESSAGE } from '../../utils/constants';
import { PasswordValidator } from '../../services/validators/PasswordValidator';
import { PasswordComplexityIndicator } from '../common/requirementManagers/PasswordComplexityIndicator';

const logManager = new LogManager('Profile');

enum InputType {
    Password = 'password',
    Text = 'text'
}

interface ProfileProperties extends ProfileInjectorProperties {}

interface ProfileState {
    attributes: Attributes;
    changePasswordModel: ChangePassword;

    editAttributesValid: boolean;
    changePasswordValid: boolean;

    showSuccessfulPasswordChange: boolean;
    showSuccessfulProfileChange: boolean;

    passwordValidationMessage: string;
    profileValidationMessage: string;

    passwordInputTypes: {
        oldPassword: InputType;
        newPassword: InputType;
    };
}

interface InputData {
    name: string;
    attributeName: UserAttributeName;
    icon: () => JSX.Element;
    miniText?: string;
    type: string;
    autocomplete: string;
}

class Profile extends React.Component<ProfileProperties, ProfileState> {

    constructor(props: any) {
        super(props);

        this.onProfileUpdated = this.onProfileUpdated.bind(this);
        this.onProfileUpdateFailed = this.onProfileUpdateFailed.bind(this);
        this.onPasswordChanged = this.onPasswordChanged.bind(this);
        this.onPasswordChangeFailed = this.onPasswordChangeFailed.bind(this);
        this.updatePassword = this.updatePassword.bind(this);
        this.onChangePasswordButtonClicked = this.onChangePasswordButtonClicked.bind(this);
        this.handleUserChange = this.handleUserChange.bind(this);
        this.submitProfile = this.submitProfile.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.isSubmitButtonDisabled = this.isSubmitButtonDisabled.bind(this);

        this.props.setOnProfileFetched(() => {
            const{firstName, lastName, companyName, title, phoneNumber} = this.props.attributes;
            this.setState({
                attributes: {
                    firstName, lastName, companyName, title, phoneNumber
                }
            });
        });

        this.state = {
            attributes: {},
            changePasswordModel: new ChangePassword(),

            editAttributesValid: true,
            changePasswordValid: true,

            showSuccessfulPasswordChange: false,
            showSuccessfulProfileChange: false,

            passwordValidationMessage: '',
            profileValidationMessage: '',

            passwordInputTypes: {
                oldPassword: InputType.Password,
                newPassword: InputType.Password
            }
        };
    }

    render() {
        const passwordValidationMsg = this.state.passwordValidationMessage;
        const passwordMsgElement = <li key={passwordValidationMsg}> {passwordValidationMsg} </li>;

        const invalidPasswordMsg = this.createWarningMessage(passwordMsgElement);
        const invalidProfileMsg = this.createWarningMessage(this.state.profileValidationMessage);
        const passwordChangedMsg = this.createCheckedMessage('Password updated successfully');
        const profileChangedMsg = this.createCheckedMessage('Profile updated successfully');

        const changePasswordSection = (
            <React.Fragment>
                <section className="col-12 offset-md-0 col-md-6 col-lg-4 offset-lg-2">
                    <div className="high__header">
                        <h1 className="title">New password</h1>
                        {this.state.showSuccessfulPasswordChange && passwordChangedMsg}
                        {!this.state.changePasswordValid && invalidPasswordMsg}
                    </div>
                    <div className="form">
                        {this.renderPasswordInput(
                            'oldPassword',
                            this.state.changePasswordModel.oldPassword,
                            this.state.passwordInputTypes.oldPassword,
                            'Current password',
                            null,
                            () => {
                                const oldPasswordType = this.state.passwordInputTypes.oldPassword === InputType.Password ?
                                    InputType.Text :
                                    InputType.Password;
                                this.setState({
                                    passwordInputTypes: {
                                        oldPassword: oldPasswordType,
                                        newPassword: this.state.passwordInputTypes.newPassword
                                    }
                                });
                            })}

                        {this.renderPasswordInput(
                            'newPassword',
                            this.state.changePasswordModel.newPassword,
                            this.state.passwordInputTypes.newPassword,
                            'New password',
                            '',
                            () => {
                                const newPasswordType = this.state.passwordInputTypes.newPassword === InputType.Password ?
                                    InputType.Text :
                                    InputType.Password;
                                this.setState({
                                    passwordInputTypes: {
                                        oldPassword: this.state.passwordInputTypes.oldPassword,
                                        newPassword: newPasswordType
                                    }
                                });
                            })}
                        <PasswordComplexityIndicator password={this.state.changePasswordModel.newPassword!}/>
                        <button disabled={!this.isSubmitButtonDisabled()} type="button" onClick={this.onChangePasswordButtonClicked} className="btn-short">Save changes</button>
                    </div>
                </section>
            </React.Fragment>
        );

        const rightSection = !authenticationService.getIsSocialAccount() && changePasswordSection;

        const controls: InputData[] = [
            { name: 'Title', attributeName: UserAttributeName.Title,  type: 'text', autocomplete: 'on', icon: () => { return PersonIcon({ witdh: '18px', height: '18px' }); } },
            { name: 'First name', attributeName: UserAttributeName.FirstName,  type: 'text', autocomplete: 'on', icon: () => { return PersonIcon({ witdh: '18px', height: '18px' }); } },
            { name: 'Last name', attributeName: UserAttributeName.LastName,  type: 'text', autocomplete: 'on', icon: () => { return PersonIcon({ witdh: '18px', height: '18px' }); } },
            { name: 'Company', attributeName: UserAttributeName.CompanyName,  type: 'text', autocomplete: 'on', icon: () => { return BriefcaseIcon({ witdh: '17px', height: '16px' }); } },
            { name: 'Phone number', attributeName: UserAttributeName.PhoneNumber,  type: 'tel', autocomplete: 'tel', icon: () => { return PhoneIcon({ witdh: '17px', height: '17px' }); }, miniText: 'Example: +14325551212' },
        ];

        return (
            <div className="container">
                <div className="row justify-content-center align-self-center">
                    <section className="col-12 col-md-6 col-lg-4">
                        <div className="high__header">
                            <h1 className="title">Edit profile</h1>
                            {this.state.showSuccessfulProfileChange && profileChangedMsg}
                            {!this.state.editAttributesValid && invalidProfileMsg}
                        </div>
                        <div className="form">
                            {controls.map((controlData) => {
                                return this.renderInput(controlData);
                            })}
                            <button type="button" onClick={this.submitProfile} className="btn-short">Save changes</button>
                        </div>
                    </section>
                    {!authenticationService.getIsSocialAccount() && rightSection}
                </div>
            </div>
        );
    }

    componentDidUpdate() {
        EventManager.fireCustomEvent(WebportalEvent.FloatLabels);
    }

    private renderPasswordInput(name: string, value: string | undefined, type: InputType, label: string, miniElement: any, toggleShowPasswordField: () => void) {
        let passwordInputFieldCssClass = this.getPasswordInputCssClass(value, name);
        return (
            <div className={passwordInputFieldCssClass}>
                <input onChange={this.updatePassword} type={type} className="form-control" placeholder={label} name={name} id={name} />
                <label htmlFor={name}>{label}</label>
                <span className="icon">
                    <LockIcon width="16px" height="19px" />
                </span>
                <div className="row">
                    <div className="row support__labels">
                        <p className="right col minitext">{miniElement}</p>
                    </div>
                </div>
            </div>
        );
    }

    private getPasswordInputCssClass(value: string | undefined, name: string) {
        return 'float-label-control password' + ((!this.isSubmitButtonDisabled() && value && name === 'newPassword' ? ' has-danger' : ''));
    }

    private renderInput(data: InputData) {
        return (
            <div key={data.name} className="float-label-control">
                <input
                    value={this.state.attributes[data.attributeName]}
                    onChange={this.handleUserChange}
                    onFocus={this.handleFocus}
                    onBlur={this.handleBlur}
                    type={data.type}
                    className="form-control"
                    placeholder={data.name}
                    autoComplete={data.autocomplete}
                    name={data.attributeName}
                    id={data.name.toLowerCase()}
                />
                <label htmlFor={data.name.toLowerCase()}>{data.name}</label>
                <span className="icon">
                    {data.icon()}
                </span>
                {
                    data.miniText ?
                        (
                            <div className="row">
                                <p className="left col minitext">
                                    {data.miniText}
                                </p>
                            </div>
                        ) :
                        null
                }
            </div>
        );
    }

    private createMessage(messageElement: JSX.Element | string, className: string, iconName: string, strokeColor: string, icon: (props: any) => JSX.Element) {
        let IconComponent = icon;
        return (
            <div className={'extended__field alert ' + className} role="alert">
                <IconComponent className="alert__icon" width="21px" />
                <p>{messageElement}</p>
            </div>
        );
    }

    private createCheckedMessage(message: JSX.Element | string): JSX.Element {
        return this.createMessage(message, 'alert-success', 'check.svg', '#ACCC58', CheckMarkIcon);
    }

    private createWarningMessage(message: JSX.Element | string): JSX.Element {
        return this.createMessage(message, 'alert-danger', 'warning-alt.svg', '#F2951F', WarningIcon);
    }

    private handleFocus(event: any) {
        let value = event.target.value ? event.target.value.trim() : '';
        if (value.length === 0) {
            return;
        }
        const property = event.target.name;
        if (property === UserAttributeName.PhoneNumber) {
            if (value.length >= 0 && value[0] !== '+' && value.indexOf('+') < 0) {
                value = '+' + value;
            }

            this.setState(prevState => {
                let attributes = prevState.attributes;
                attributes[property] = value;
                return {
                    attributes,
                    showSuccessfulProfileChange: false
                };
            });
        }
    }

    private handleBlur(event: any) {
        let value = event.target.value ? event.target.value.trim() : '';
        if (value.length === 0 ) {
            return;
        }
        const property = event.target.name;

        if (property === UserAttributeName.PhoneNumber) {
            if (value.length === 1 && value === '+') {
                value = '';
            }
        }

        this.setState(prevState => {
            let attributes = prevState.attributes;
            attributes[property] = value;
            return {
                attributes,
                showSuccessfulProfileChange: false
            };
        });
    }

    private handleUserChange(event: any) {
        let value: string = event.target.value;
        const property = event.target.name;

        if (property === UserAttributeName.PhoneNumber) {
            if (value[0] !== '+' && value.indexOf('+') < 0) {
                value = '+' + value;
            }
        }

        this.setState(prevState => {
            let attributes = prevState.attributes;
            attributes[property] = value;
            return {
                attributes,
                showSuccessfulProfileChange: false
            };
        });
    }

    private submitProfile(event: any) {
        event.preventDefault();
        event.stopPropagation();
        const attributes = this.state.attributes;
        for (const param in attributes) {
            if (attributes[param] === null) {
                delete attributes[param];
            }
        }
        authenticationService.updateAttributes(attributes)
            .then(this.onProfileUpdated)
            .catch(this.onProfileUpdateFailed);
    }

    private onProfileUpdated() {
        this.setState({
            editAttributesValid: true,
            showSuccessfulProfileChange: true
        });
        this.props.fetchProfile(false);
    }

    private onProfileUpdateFailed(err: any) {
        errorHandler(err);
        this.setState({
            editAttributesValid: false,
            showSuccessfulProfileChange: false,
            profileValidationMessage: err.message || err.body.message
        });
    }

    private updatePassword(event: any) {
        let value = event.target.value.trim();
        let target = event.target.name;
        this.updateChangePasswordModel(target, value);
    }

    private updateChangePasswordModel(target: any, value: any) {
        this.setState(prevState => {
            let changePasswordModel = prevState.changePasswordModel;
            changePasswordModel[target] = value;
            return { changePasswordModel };
        });
    }

    private isSubmitButtonDisabled(): boolean {
        const oldPassword = this.state.changePasswordModel.oldPassword;
        const newPassword = this.state.changePasswordModel.newPassword;
        if (!oldPassword || !newPassword) {
            return false;
        }
        return oldPassword.length > 0 && PasswordValidator.validatePassword(newPassword);
    }

    private onChangePasswordButtonClicked(event: any): void {
        event.preventDefault();
        event.stopPropagation();
        let changePasswordModel = this.state.changePasswordModel;
        authenticationService.changePassword(changePasswordModel)
                .then(this.onPasswordChanged)
                .catch(this.onPasswordChangeFailed);
    }

    private onPasswordChanged() {
        let changePasswordModel = new ChangePassword();

        this.setState({
            changePasswordValid: true,
            showSuccessfulPasswordChange: true,
            changePasswordModel
        });
    }

    private onPasswordChangeFailed(err: any): void {
        logManager.error(err);
        let passwordValidationMessage;
        switch (err.code) {
            case CognitoException.NotAuthorizedException:
                passwordValidationMessage = CognitoResetPasswordExceptionMessage.IncorrectPassword ;
                break;
            case CognitoException.LimitExceededException:
                passwordValidationMessage = CognitoResetPasswordExceptionMessage.LimitExceeded ;
                break;
            default:
                passwordValidationMessage = DEFAULT_ERROR_MESSAGE;

        }
        this.setState({
            changePasswordValid: false,
            showSuccessfulPasswordChange: false,
            passwordValidationMessage
        });
    }
}

export default injectProfile(Profile);



// WEBPACK FOOTER //
// ./src/webportal/components/profile/Profile.tsx